import React, { useMemo, useState } from 'react';
import { Responsive, WidthProvider, Layout as RGLLayout } from 'react-grid-layout';
import { WidgetContainer } from './WidgetContainer';
import { WidgetId, WidgetLayout, Role } from '@/types'; 
import { WIDGET_CONSTRAINTS } from '../utils/layoutDefaults';
import clsx from 'clsx';

// 🏗️ Higher-order component to inject responsive width detection
const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardGridProps {
  layouts: WidgetLayout[];
  isEditMode: boolean;
  onLayoutChange: (currentLayout: RGLLayout[]) => void;
  onRemoveWidget: (id: string) => void;
  widgetRegistry: Partial<Record<WidgetId, React.ReactNode>>;
  
  // 🛡️ Hybrid Scope Props
  currentUserId: string;
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * 🛰️ DashboardGrid
 * Refactored for Hybrid Scoping: The layout itself is personal (/users/{uid}),
 * but widgets consume data based on the activeScope toggle.
 */
export const DashboardGrid: React.FC<DashboardGridProps> = ({
  layouts,
  isEditMode,
  onLayoutChange,
  onRemoveWidget,
  widgetRegistry,
  currentUserId,
  currentHouseholdId,
  activeScope
}) => {

  /**
   * 🧠 Layout Transformer
   * Converts WidgetLayout[] to RGL's Layout[] format.
   * Layouts are unique to the user's personal partition.
   */
  const rglLayout = useMemo(() => {
    return (layouts || []).map((w: WidgetLayout) => ({
      i: w.i,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      ...WIDGET_CONSTRAINTS[w.i as WidgetId] 
    }));
  }, [layouts]);

  return (
    <div className={clsx(
      "flex-1 transition-all duration-700 min-h-screen relative",
      isEditMode ? 'bg-slate-50/80 dark:bg-slate-950/40' : 'bg-transparent'
    )}>
      
      {/* --- GRID ENGINE --- */}
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: rglLayout, md: rglLayout }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 48, md: 36, sm: 24, xs: 12, xxs: 8 }}
        rowHeight={36}
        draggableHandle=".drag-handle"
        isDraggable={isEditMode}
        isResizable={isEditMode}
        margin={[8, 8]}
        onLayoutChange={onLayoutChange}
        useCSSTransforms={true}
        compactType={null}
        preventCollision={true}
      >
        {layouts.map((widget: WidgetLayout) => (
          <div key={widget.i} className="relative">
            <WidgetContainer
              id={widget.i as WidgetId}
              title={(widget.i || '').replace(/_/g, ' ')}
              isEditMode={isEditMode}
              onRemove={onRemoveWidget}
              dragHandleProps={{ className: 'drag-handle' }}
              // 🎨 Scope-aware container styling
              className={clsx(
                activeScope === 'PERSONAL' && "border-emerald-500/20",
                activeScope === 'SHARED' && "border-brand/20"
              )}
            >
              {/* 🧩 Content Injection with Scope Awareness */}
              {widgetRegistry[widget.i as WidgetId] || (
                <div className="flex flex-col items-center justify-center h-full opacity-20 italic">
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400">
                    Module Offline
                  </p>
                </div>
              )}
            </WidgetContainer>
          </div>
        ))}
      </ResponsiveGridLayout>

      {/* --- EDIT MODE VISUAL GUIDES --- */}
      {isEditMode && (
        <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.05] dark:opacity-[0.3] transition-opacity duration-1000">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
              backgroundSize: `32px 32px`,
              color: '#5e88fc'
            }}
          />
        </div>
      )}
    </div>
  );
};